import { useCallback, useEffect, useState } from 'react'

import { ReportFileButton } from 'app/common/templates-next/report-template'

import { useGenerateFile, useReportCancel, useReportCheckStatus } from 'app/dataProviders/reports/hooks'
import FileExtractor from './file-extractor'
import { ReportFileFormat } from 'app/common/types/reports/generateFile'
import { iconType } from 'app/common/ui-next/plain-icon'

type Props = {
  reportID: string
  filenameBase: string
  format?: ReportFileFormat
  formatDesc?: string
}

export const ReportFile = ({
  reportID,
  filenameBase,
  format = ReportFileFormat.XLSX,
  formatDesc = 'документ Word',
}: Props) => {
  const [generatedFileID, setGeneratedFileID] = useState<string | null>(null)
  const [reportFileID, setReportFileID] = useState<string | null>(null)
  const filename = `${filenameBase}_${reportFileID || generatedFileID}`
  const {
    onFileDownload,
    getGenerateFile,
  } = useGenerateFile({
    filename,
    fileFormat: format,
  })
  const onProcessCheckStatus = useReportCheckStatus(reportFileID)
  const onReportCancel = useReportCancel(reportFileID)
  const [pending, setPending] = useState(false)


  const handleGeneratedFileID = useCallback((id: string) => {
    setPending(false)
    setReportFileID(null)
    setGeneratedFileID(id)
  }, [])

  const handleClick = useCallback(async () => {
    setPending(true)
    if (generatedFileID) {
      onFileDownload(generatedFileID)
      setPending(false)
      return
    }
    const res = await getGenerateFile(reportID)
    if (res) {
      setReportFileID(res)
    }
  }, [generatedFileID, onFileDownload, getGenerateFile, reportID])

  const handleCheckStatus = useCallback(async () => {
    if (reportFileID) {
      const res = await onProcessCheckStatus()
      if (typeof res === 'string') {
        return parseInt(res, 10)
      }
      return res
    }
    return null
  }, [reportFileID, onProcessCheckStatus])

  const handleCancel = useCallback(() => {
    onReportCancel()
    setPending(false)
    setReportFileID(null)
  }, [onReportCancel])

  useEffect(() => {
    setReportFileID(null)
    setGeneratedFileID(null)
  }, [reportID])

  const tooltipPrompt = (
    generatedFileID
      ? `Скачать в формате «${formatDesc}»`
      : `Сгенерировать и скачать в формате «${formatDesc}»`
  )


  const iconFormat: Record<ReportFileFormat, (typeof iconType)[keyof typeof iconType]> = {
    [ReportFileFormat.XLSX]: iconType.EXCEL,
    [ReportFileFormat.ZIP]: iconType.ZIP,
    [ReportFileFormat.DOCX]: iconType.WORD
  }

  return (
    <>
      <ReportFileButton
        tooltipPrompt={tooltipPrompt}
        format={iconFormat[format]}
        label="Скачать как"
        loading={pending}
        onClick={handleClick}
      />

      {reportFileID
        && !generatedFileID
        && <FileExtractor
          reportFileID={reportFileID}
          onGeneratedFileID={handleGeneratedFileID}
          onCheckStatus={handleCheckStatus}
          onCancel={handleCancel}
          onFileDownload={onFileDownload}
        />}
    </>
  )
}
